import { Box, Typography, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import ExoForm from '../../../components/exo/ExoForm';
import { useTranslation } from 'react-i18next';
import ExoDialog from '../../../components/exo/ExoDialog';
import { Status } from '../../../components/special/Status';
import { tokens } from '../../../global/theme/tokens';
import { API_PATCH, API_POST, DBRequest, useConfig } from '../../../api/api';
import * as Yup from 'yup';

export const NewLeadStatus = ({
  open,
  onClose,
  onCreate,
  onUpdate,
  startData,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const config = useConfig();
  const [data, setData] = useState(null);
  const defaultData = {
    label: t('New Status'),
    color: '#daba44',
    variant: 'base',
  };
  const [startDataCollection, setStartDataCollection] = useState(defaultData);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setData(startData || defaultData);
    setStartDataCollection(startData || defaultData);
  }, [startData]);

  const fields = [
    {
      label: t('Color'),
      key: 'color',
      type: 'color',
    },
    {
      label: t('Name'),
      key: 'label',
      type: 'text',
    },
    {
      label: t('Variant'),
      key: 'variant',
      type: 'customSelect',
      noUnselect: true,
      options: [
        {
          label: (
            <Status variant="base" text={t('Base')} statusColor={colors.text} />
          ),
          value: 'base',
        },
        {
          label: (
            <Status
              variant="draft"
              text={t('Draft')}
              statusColor={colors.text}
            />
          ),
          value: 'draft',
        },
        {
          label: (
            <Status variant="open" text={t('Open')} statusColor={colors.text} />
          ),
          value: 'open',
        },
        {
          label: (
            <Status variant="sent" text={t('Sent')} statusColor={colors.text} />
          ),
          value: 'sent',
        },
        {
          label: (
            <Status
              variant="accepted"
              text={t('Checked')}
              statusColor={colors.text}
            />
          ),
          value: 'accepted',
        },
        {
          label: (
            <Status
              variant="rejected"
              text={t('Canceled')}
              statusColor={colors.text}
            />
          ),
          value: 'rejected',
        },
        {
          label: (
            <Status
              variant="expired"
              text={t('Outdated')}
              statusColor={colors.text}
            />
          ),
          value: 'expired',
        },
        {
          label: (
            <Status
              variant="legal"
              text={t('Legal')}
              statusColor={colors.text}
            />
          ),
          value: 'legal',
        },
        {
          label: (
            <Status
              variant="finished"
              text={t('Finished')}
              statusColor={colors.text}
            />
          ),
          value: 'finished',
        },
      ],
    },
  ];

  function handleChange(dataCol) {
    setData(dataCol);
  }

  function handleSubmit(reqData) {
    const isUpdate = Boolean(reqData.id);
    DBRequest({
      config,
      path: isUpdate ? `lead-statuses/${reqData.id}` : `lead-statuses`,
      method: isUpdate ? API_PATCH(reqData) : API_POST(reqData),
      onResponse: isUpdate ? onUpdate : onCreate,
      onLoading: setIsLoading,
    });
  }

  const validationSchema = Yup.object().shape({
    color: Yup.string().required('Is required'),
    label: Yup.string().required('Is required'),
    variant: Yup.string().required('Is required'),
  });

  return (
    <ExoDialog open={open} onClose={onClose} limitWidth maxWidth="400px">
      <Box className="flex flex-col gap-2">
        <Typography variant="h3" textAlign="center" className="  py-2">
          {t('New Status')}
        </Typography>
        <Box className="pb-2">
          <Typography variant="h6">{t('preview')}</Typography>
          <Box className="rounded-md overflow-hidden shadow-md">
            <StatusPreview data={data} mode="light" />
            <StatusPreview data={data} mode="dark" />
          </Box>
          <Typography fontSize={11} className="pt-1">
            {t(
              'Make sure that the status is clearly legible in both light and dark mode'
            )}
          </Typography>
        </Box>

        <ExoForm
          fields={fields}
          onSubmit={handleSubmit}
          onChange={handleChange}
          onCancel={onClose}
          validationSchema={validationSchema}
          startDataCollection={startDataCollection}
          isLoading={isLoading}
          noDifferences
        />
      </Box>
    </ExoDialog>
  );
};

const StatusPreview = ({ mode, data }) => {
  const theme = useTheme();
  const colors = tokens(mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  return (
    <Box sx={{ bgcolor: colors.glass }} className="px-2 py-1">
      <Typography sx={{ color: colors.text }} fontSize={10} fontWeight={500}>
        {t(mode)}
      </Typography>
      <Status
        variant={data?.variant}
        text={data?.label}
        statusColor={data?.color || colors.text}
        colorMode={mode}
      />
    </Box>
  );
};
